import { getPublishedPageBySlug, getPagesByAccountId } from './firestoreService';
import { getSubdomainBySubdomain } from './subdomainService';
import { doc, getDoc } from 'firebase/firestore';
import { db } from './firebase';

/**
 * ページデータから完全なHTMLファイルを生成します
 * @param {Object} pageData - ページデータ
 * @param {string} subdomain - サブドメイン（オプション）
 * @param {Object} settings - グローバル設定（オプション）
 * @returns {string} 完全なHTMLファイルの内容
 */
export const generatePageHTML = (pageData, subdomain = null, settings = null) => {

    const { head, body, css, title, description, jsonld, content, html } = pageData;

    // 完全なHTMLファイルが提供されている場合、それを直接使用するかチェック
    const hasCompleteHTML = (content || html) &&
        (content || html).includes('<!DOCTYPE') &&
        (content || html).includes('<html') &&
        (content || html).includes('</html>');

    if (hasCompleteHTML) {
        let completeHTML = content || html;

        // ページ固有のカスタムheadを挿入
        if (pageData.customHead) {
            completeHTML = completeHTML.replace(
                /<\/head>/i,
                `\n<!-- Page Custom Head -->\n${pageData.customHead}\n</head>`
            );
        }

        // ページ固有のカスタムbodyを挿入
        if (pageData.customBody) {
            completeHTML = completeHTML.replace(
                /<body([^>]*)>/i,
                `<body$1>\n<!-- Page Custom Body -->\n${pageData.customBody}`
            );
        }

        // グローバル設定のcustomHeadを既存のhead要素に挿入
        if (settings && settings.customHead) {
            // headタグの終了直前にcustomHeadを挿入
            completeHTML = completeHTML.replace(
                /<\/head>/i,
                `\n<!-- Global Settings Custom Head -->\n${settings.customHead}\n</head>`
            );
        } else {
        }

        // グローバル設定のcustomBodyを既存のbody要素に挿入
        if (settings && settings.customBody) {
            console.log('🔧 グローバル設定のcustomBodyを挿入:', {
                customBodyLength: settings.customBody.length,
                customBodyPreview: settings.customBody.substring(0, 200) + '...'
            });
            // bodyタグの開始直後にcustomBodyを挿入
            completeHTML = completeHTML.replace(
                /<body([^>]*)>/i,
                `<body$1>\n<!-- Global Settings Custom Body -->\n${settings.customBody}`
            );
            console.log('✅ customBody挿入完了');
        } else {
            console.log('⚠️ customBodyの挿入をスキップ:', {
                hasSettings: !!settings,
                hasCustomBody: !!(settings && settings.customBody)
            });
        }

        // サブドメインの場合のみリンク修正
        if (subdomain) {
            completeHTML = completeHTML
                .replace(/href="\/pages\/([^"]+)"/g, `href="https://${subdomain}.suisui.life/$1`)
                .replace(/href="https:\/\/suisui\.life\/pages\/([^"]+)"/g, `href="https://${subdomain}.suisui.life/$1`);
            console.log('🔗 サブドメイン用にリンクを修正しました');
        }

        return completeHTML;
    }

    console.log('🔧 HTMLファイルを再構築します');

    // bodyコンテンツの決定：body > content > html から抽出
    let bodyContent = body;

    if (!bodyContent && content) {
        // contentからbodyを抽出
        const bodyMatch = content.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
        bodyContent = bodyMatch ? bodyMatch[1].trim() : content;
    }

    if (!bodyContent && html) {
        // htmlからbodyを抽出
        const bodyMatch = html.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
        bodyContent = bodyMatch ? bodyMatch[1].trim() : html;
    }

    console.log('🎨 bodyコンテンツ決定:', {
        hasBody: !!body,
        hasContent: !!content,
        hasHtml: !!html,
        bodyContentLength: bodyContent?.length || 0,
        bodyPreview: bodyContent?.substring(0, 200) + '...',
        contentPreview: content?.substring(0, 200) + '...',
        htmlPreview: html?.substring(0, 200) + '...'
    });

    // ベースURLの設定
    const baseUrl = subdomain ? `https://${subdomain}.suisui.life` : 'https://suisui.life';

    // headコンテンツの処理
    let processedHead = head || '';

    // 基本的なメタタグが不足している場合は追加
    if (!processedHead.includes('<meta charset="UTF-8">')) {
        processedHead = `<meta charset="UTF-8">\n${processedHead}`;
    }
    if (!processedHead.includes('<meta name="viewport"')) {
        processedHead = `<meta name="viewport" content="width=device-width, initial-scale=1.0">\n${processedHead}`;
    }

    // タイトルタグの処理
    if (!processedHead.includes('<title>')) {
        processedHead = `<title>${title || 'ページ'}</title>\n${processedHead}`;
    }

    // 説明メタタグの処理
    if (description && !processedHead.includes('name="description"')) {
        processedHead = `<meta name="description" content="${description}">\n${processedHead}`;
    }

    // CSSの追加
    if (css) {
        processedHead += `\n<style>\n${css}\n</style>`;
    }

    // JSON-LDの追加
    if (jsonld) {
        processedHead += `\n<script type="application/ld+json">\n${JSON.stringify(jsonld, null, 2)}\n</script>`;
    }

    // カラーパレット用のCSS読み込みリンクを追加（パレット無効化されていない場合のみ）
    if (!pageData.disableColorPalette) {
        const paletteToUse = pageData.colorPalette || (settings && settings.defaultPalette) || 'default';
        let cssUrl = `/api/generate-palette-css?palette=${paletteToUse}`;

        // カスタムパレットがある場合はURLパラメータに追加
        if (settings && settings.customPalettes && Object.keys(settings.customPalettes).length > 0) {
            const customPalettesParam = encodeURIComponent(JSON.stringify(settings.customPalettes));
            cssUrl += `&customPalettes=${customPalettesParam}`;
        }

        const cssLinkTag = `<link rel="stylesheet" href="${cssUrl}" id="color-palette-css">`;
        processedHead += `\n<!-- Color Palette CSS -->\n${cssLinkTag}`;
        console.log('🎨 カラーパレットCSS読み込みを追加:', paletteToUse);
    } else {
        console.log('🚫 カラーパレットが無効化されているため、CSS読み込みをスキップ');
    }

    // ページ固有のカスタムheadを追加
    if (pageData.customHead) {
        console.log('🔧 ページ固有のcustomHeadを追加:', {
            customHeadLength: pageData.customHead.length,
            customHeadPreview: pageData.customHead.substring(0, 200) + '...'
        });
        processedHead += `\n<!-- Page Custom Head -->\n${pageData.customHead}`;
        console.log('✅ ページ固有customHead追加完了');
    }

    // グローバル設定のcustomHeadを追加
    if (settings && settings.customHead) {
        console.log('🔧 グローバル設定のcustomHeadを追加:', {
            customHeadLength: settings.customHead.length,
            customHeadPreview: settings.customHead.substring(0, 200) + '...'
        });
        processedHead += `\n<!-- Global Settings Custom Head -->\n${settings.customHead}`;
        console.log('✅ customHead追加完了');
    } else {
        console.log('⚠️ customHeadの追加をスキップ:', {
            hasSettings: !!settings,
            hasCustomHead: !!(settings && settings.customHead)
        });
    }

    // サブドメインの場合、ページ内のリンクを修正
    let processedBody = bodyContent || '';

    // ページ固有のカスタムbodyを追加
    if (pageData.customBody) {
        console.log('🔧 ページ固有のcustomBodyを追加:', {
            customBodyLength: pageData.customBody.length,
            customBodyPreview: pageData.customBody.substring(0, 200) + '...'
        });
        processedBody = `<!-- Page Custom Body -->\n${pageData.customBody}\n${processedBody}`;
        console.log('✅ ページ固有customBody追加完了');
    }

    // グローバル設定のcustomBodyを追加
    if (settings && settings.customBody) {
        console.log('🔧 グローバル設定のcustomBodyを追加:', {
            customBodyLength: settings.customBody.length,
            customBodyPreview: settings.customBody.substring(0, 200) + '...'
        });
        processedBody = `<!-- Global Settings Custom Body -->\n${settings.customBody}\n${processedBody}`;
        console.log('✅ customBody追加完了');
    } else {
        console.log('⚠️ customBodyの追加をスキップ:', {
            hasSettings: !!settings,
            hasCustomBody: !!(settings && settings.customBody)
        });
    }

    if (subdomain) {
        // 相対リンクをサブドメイン用に修正（/pages/を削除）
        processedBody = processedBody.replace(
            /href="\/pages\/([^"]+)"/g,
            `href="${baseUrl}/$1"`
        );
        // 絶対パスのリンクも修正（/pages/を削除）
        processedBody = processedBody.replace(
            /href="https:\/\/suisui\.life\/pages\/([^"]+)"/g,
            `href="${baseUrl}/$1"`
        );
    } else {
        // メインドメインの場合も/pages/を削除
        processedBody = processedBody.replace(
            /href="\/pages\/([^"]+)"/g,
            `href="/$1"`
        );
        // 絶対パスのリンクも修正
        processedBody = processedBody.replace(
            /href="https:\/\/suisui\.life\/pages\/([^"]+)"/g,
            `href="https://suisui.life/$1"`
        );
    }

    // 完全なHTMLファイルを生成
    const pageHtml = `<!DOCTYPE html>
<html lang="ja">
<head>
${processedHead}
</head>
<body>
${processedBody}
</body>
</html>`;

    console.log('🎨 最終HTML生成:', {
        processedHeadLength: processedHead?.length || 0,
        processedBodyLength: processedBody?.length || 0,
        finalHtmlLength: pageHtml?.length || 0,
        processedHeadPreview: processedHead?.substring(0, 100) + '...',
        processedBodyPreview: processedBody?.substring(0, 200) + '...'
    });

    return pageHtml;
};

/**
 * サブドメインページのHTMLを生成します
 * @param {Object} subdomainData - サブドメインデータ
 * @param {Array} pages - ページ一覧
 * @returns {string} 完全なHTMLファイルの内容
 */
export const generateSubdomainHTML = (subdomainData, pages) => {
    const { subdomain } = subdomainData;
    const publicPages = pages.filter(page => page.isPublished);
    const baseUrl = `https://${subdomain}.suisui.life`;

    const pagesListHTML = publicPages.length > 0
        ? publicPages.map(page => `
            <div class="page-card">
                <h3>${page.title}</h3>
                <p>${page.description || '説明なし'}</p>
                <div class="page-meta">
                    <span>最終更新: ${new Date(page.updatedAt.toDate()).toLocaleDateString('ja-JP')}</span>
                </div>
                <a href="${baseUrl}/${page.slug}" class="view-button">ページを見る</a>
            </div>
        `).join('')
        : `
            <div class="empty-state">
                <h2>まだページが公開されていません</h2>
                <p>このサブドメインのオーナーがページを作成・公開するまでお待ちください。</p>
            </div>
        `;

    const subdomainHtml = `<!DOCTYPE html>
<html lang="ja">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${subdomain}.suisui.life</title>
    <meta name="description" content="${subdomain}.suisui.life のウェブサイト">
    <style>
        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            line-height: 1.6;
            margin: 0;
            padding: 0;
            background-color: #f5f5f5;
        }
        .container {
            max-width: 1200px;
            margin: 0 auto;
            padding: 20px;
        }
        .header {
            text-align: center;
            padding: 40px 0;
            background: white;
            border-radius: 8px;
            margin-bottom: 30px;
            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }
        .header h1 {
            margin: 0;
            color: #333;
            font-size: 2.5em;
        }
        .header p {
            margin: 10px 0 0 0;
            color: #666;
        }
        .pages-list {
            background: white;
            padding: 30px;
            border-radius: 8px;
            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }
        .pages-list h2 {
            margin-top: 0;
            color: #333;
        }
        .pages-grid {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
            gap: 20px;
            margin-top: 20px;
        }
        .page-card {
            border: 1px solid #e0e0e0;
            border-radius: 8px;
            padding: 20px;
            transition: transform 0.2s;
        }
        .page-card:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 8px rgba(0,0,0,0.1);
        }
        .page-card h3 {
            margin: 0 0 10px 0;
            color: #333;
        }
        .page-card p {
            margin: 0 0 15px 0;
            color: #666;
        }
        .page-meta {
            font-size: 0.9em;
            color: #999;
            margin-bottom: 15px;
        }
        .view-button {
            display: inline-block;
            padding: 8px 16px;
            background: #007bff;
            color: white;
            text-decoration: none;
            border-radius: 4px;
            transition: background 0.2s;
        }
        .view-button:hover {
            background: #0056b3;
        }
        .empty-state {
            text-align: center;
            padding: 40px;
            color: #666;
        }
        .footer {
            text-align: center;
            margin-top: 40px;
            padding: 20px;
            color: #666;
        }
        .footer a {
            color: #007bff;
            text-decoration: none;
        }
    </style>
</head>
<body>
    <div class="container">
        <header class="header">
            <h1>${subdomain}.suisui.life</h1>
            <p>Suisuiで作成されたウェブサイト</p>
        </header>

        <main class="pages-list">
            <h2>公開ページ一覧</h2>
            <div class="pages-grid">
                ${pagesListHTML}
            </div>
        </main>

        <footer class="footer">
            <p>Powered by <a href="https://suisui.life" target="_blank" rel="noopener noreferrer">Suisui</a></p>
        </footer>
    </div>
</body>
</html>`;

    return subdomainHtml;
};

/**
 * 指定されたページのHTMLファイルを生成してFirebase Storageに保存します
 * @param {string} pageId - ページID
 * @param {string} slug - ページスラッグ
 * @param {string} subdomain - サブドメイン（オプション）
 * @param {string} accountId - アカウントID（settings取得用、オプション）
 * @returns {Promise<string>} 生成されたHTMLファイルのURL
 */
export const generateAndSavePageHTML = async (pageId, slug, subdomain = null, accountId = null) => {
    try {

        // ページデータを取得
        const pageData = await getPublishedPageBySlug(slug);

        if (!pageData) {
            console.error('❌ ページが見つかりません:', slug);
            throw new Error('ページが見つかりません');
        }

        // グローバル設定を取得
        let settings = null;
        try {
            const targetAccountId = accountId || pageData.accountId;
            if (targetAccountId) {
                console.log('🔧 グローバル設定を取得:', targetAccountId);
                const settingsDoc = await getDoc(doc(db, 'settings', targetAccountId));
                if (settingsDoc.exists()) {
                    settings = settingsDoc.data();
                    console.log('✅ グローバル設定取得完了');
                } else {
                    console.log('ℹ️ グローバル設定が見つかりません');
                }
            }
        } catch (settingsError) {
            if (settingsError.code === 'permission-denied') {
                console.warn('🚫 グローバル設定へのアクセス権限がありません');
            } else {
                console.warn('⚠️ グローバル設定の取得に失敗:', settingsError);
            }
        }

        // HTMLを生成
        console.log('🎨 HTML生成中...');
        const html = generatePageHTML(pageData, subdomain, settings);
        console.log('✅ HTML生成完了:', html.length, '文字');

        // Firebase Storageに保存
        try {
            const { ref, uploadString, getDownloadURL } = await import('firebase/storage');
            const { storage } = await import('./firebase');

            const fileName = subdomain
                ? `subdomains/${subdomain}/pages/${slug}.html`
                : `pages/${slug}.html`;

            console.log('💾 保存先:', fileName);

            const storageRef = ref(storage, fileName);
            await uploadString(storageRef, html, 'raw');
            console.log('✅ Firebase Storageに保存完了');

            // 公開URLを取得
            const downloadURL = await getDownloadURL(storageRef);
            console.log('🔗 ダウンロードURL:', downloadURL);

            return downloadURL;
        } catch (storageError) {
            console.error('⚠️ Firebase Storage操作エラー:', storageError);
            // Storage操作に失敗した場合でもHTMLは正常に生成されているので、
            // 代替URLまたはnullを返す
            return null;
        }
    } catch (error) {
        console.error('HTMLファイル生成エラー:', error);
        throw error;
    }
};

/**
 * 渡されたページデータを使用してHTMLファイルを生成してFirebase Storageに保存します
 * @param {Object} pageData - ページデータ
 * @param {string} slug - ページスラッグ
 * @param {string} subdomain - サブドメイン（オプション）
 * @param {string} accountId - アカウントID（settings取得用、オプション）
 * @returns {Promise<string>} 生成されたHTMLファイルのURL
 */
export const generateAndSavePageHTMLWithData = async (pageData, slug, subdomain = null, accountId = null) => {
    try {

        // グローバル設定を取得
        let settings = null;
        try {
            const targetAccountId = accountId || pageData.accountId;
            if (targetAccountId) {
                console.log('🔧 グローバル設定を取得:', targetAccountId);
                const settingsDoc = await getDoc(doc(db, 'settings', targetAccountId));
                if (settingsDoc.exists()) {
                    settings = settingsDoc.data();
                    console.log('✅ グローバル設定取得完了:', {
                        hasCustomHead: !!settings.customHead,
                        customHeadLength: settings.customHead?.length || 0,
                        customHeadPreview: settings.customHead?.substring(0, 100) + '...',
                        settingsKeys: Object.keys(settings)
                    });
                } else {
                    console.log('ℹ️ グローバル設定が見つかりません');
                }
            } else {
                console.log('⚠️ アカウントIDが見つかりません:', { accountId, pageDataAccountId: pageData.accountId });
            }
        } catch (settingsError) {
            if (settingsError.code === 'permission-denied') {
                console.warn('🚫 グローバル設定へのアクセス権限がありません');
            } else {
                console.warn('⚠️ グローバル設定の取得に失敗:', settingsError);
            }
        }

        // HTMLを生成
        console.log('🎨 HTML生成中...');
        const html = generatePageHTML(pageData, subdomain, settings);
        console.log('✅ HTML生成完了:', html.length, '文字');

        // Firebase Storageに保存
        try {
            const { ref, uploadString, getDownloadURL } = await import('firebase/storage');
            const { storage } = await import('./firebase');

            const fileName = subdomain
                ? `subdomains/${subdomain}/pages/${slug}.html`
                : `pages/${slug}.html`;

            console.log('💾 保存先:', fileName);

            const storageRef = ref(storage, fileName);
            await uploadString(storageRef, html, 'raw');
            console.log('✅ Firebase Storageに保存完了');

            // 公開URLを取得
            const downloadURL = await getDownloadURL(storageRef);
            console.log('🔗 ダウンロードURL:', downloadURL);

            return downloadURL;
        } catch (storageError) {
            console.error('⚠️ Firebase Storage操作エラー:', storageError);
            // Storage操作に失敗した場合でもHTMLは正常に生成されているので、
            // 代替URLまたはnullを返す
            return null;
        }
    } catch (error) {
        console.error('HTMLファイル生成エラー:', error);
        throw error;
    }
};

/**
 * サブドメインのHTMLファイルを生成してFirebase Storageに保存します
 * @param {string} subdomain - サブドメイン
 * @returns {Promise<string>} 生成されたHTMLファイルのURL
 */
export const generateAndSaveSubdomainHTML = async (subdomain) => {
    try {
        // サブドメインデータを取得
        const subdomainData = await getSubdomainBySubdomain(subdomain);
        if (!subdomainData) {
            throw new Error('サブドメインが見つかりません');
        }

        // ページ一覧を取得
        const pages = await getPagesByAccountId(subdomainData.accountId);

        // HTMLを生成
        const html = generateSubdomainHTML(subdomainData, pages);

        // Firebase Storageに保存
        const { ref, uploadString } = await import('firebase/storage');
        const { storage } = await import('./firebase');

        const fileName = `subdomains/${subdomain}/index.html`;
        const storageRef = ref(storage, fileName);
        await uploadString(storageRef, html, 'raw');

        // 公開URLを取得
        const { getDownloadURL } = await import('firebase/storage');
        const downloadURL = await getDownloadURL(storageRef);

        return downloadURL;
    } catch (error) {
        console.error('サブドメインHTMLファイル生成エラー:', error);
        throw error;
    }
}; 